import { $FixMe } from '@eventbrite/ts-utils';
import classNames from 'classnames';
import camelCase from 'lodash/camelCase';
import find from 'lodash/find';
import includes from 'lodash/includes';
import mapKeys from 'lodash/mapKeys';
import {
    BUCKET_WRAPPER_WITH_DECORATION_CLASSES,
    CATEGORY_TEMPLATE,
    COLLECTION_TEMPLATE,
    DECORATIVE_IMAGE_PLACEMENT_MAP,
    EDITORIAL_ARTICLES_BUCKET_KEY,
    ENTITY_TYPE_MAP,
    EVENT_TIME_OPTIONS_TEMPLATE,
    NEARBY_CITIES_TEMPLATE,
    NON_EVENT_BUCKET_SPACING_CLASSES,
    ORGANIZER_FOLLOW_TEMPLATE,
    SHARED_BUCKET_SPACING_CLASSES,
    TRENDING_SEARCHES_TEMPLATE,
} from '../../../../constants';
import {
    Bucket,
    BucketsContent,
    ThingsToDoLocationData,
    ThingsToDoPlaceData,
} from '../../../../types';
import { CARD_INFO_LOOKUP_MAP } from '../../../../utils/general';

const NON_EVENT_BUCKETS = [
    CATEGORY_TEMPLATE,
    NEARBY_CITIES_TEMPLATE,
    TRENDING_SEARCHES_TEMPLATE,
    ORGANIZER_FOLLOW_TEMPLATE,
    EVENT_TIME_OPTIONS_TEMPLATE,
];
/*
 Returns the decorativeImagePlacement matching the selector and adds it to an array to prevent duplicates
*/
const _getDecorativeImagePlacement = (
    key: string,
    usedDecorativeImages: Array<string | string[]>,
) => {
    const decorativeImage = find(
        DECORATIVE_IMAGE_PLACEMENT_MAP,
        (decorativeImageData) => {
            const { selectors } = decorativeImageData;

            let isKeyMappedInPlacementMap;

            if (!includes(usedDecorativeImages, selectors)) {
                isKeyMappedInPlacementMap = includes(selectors, key);

                if (isKeyMappedInPlacementMap) {
                    usedDecorativeImages.push(selectors);
                }
            }

            return isKeyMappedInPlacementMap;
        },
    );

    return decorativeImage || undefined;
};

export const getDecorativeImage = (
    decorativeImages: { [key: string]: string },
    key: string,
    usedImages: Array<string | string[]>,
) => {
    let decorativeImage;

    const decorationPlacement: $FixMe = _getDecorativeImagePlacement(
        key,
        usedImages,
    );

    if (decorationPlacement) {
        decorativeImage = decorativeImages[decorationPlacement.urlSelector];
    }

    return { decorationPlacement, decorativeImage };
};

export const getBucketClasses = (
    key: string,
    decorativeImage: string,
    template: string,
) => {
    // Only the articles and collections section should have a gray background
    const shouldHaveBackground =
        key === EDITORIAL_ARTICLES_BUCKET_KEY || key === COLLECTION_TEMPLATE;

    const containerClasses = classNames({
        'eds-bg-color--grey-100': shouldHaveBackground,
    });

    let spacingClasses = SHARED_BUCKET_SPACING_CLASSES;

    if (includes(NON_EVENT_BUCKETS, template)) {
        spacingClasses = NON_EVENT_BUCKET_SPACING_CLASSES;
    }

    const bucketWrapperClasses = classNames(spacingClasses, {
        [BUCKET_WRAPPER_WITH_DECORATION_CLASSES]: decorativeImage,
        'collection-bucket-top-spacing': key === COLLECTION_TEMPLATE,
    });

    return { containerClasses, bucketWrapperClasses };
};

export const prepareThingsToDoShelfData = (
    buckets: BucketsContent,
    locationData: ThingsToDoLocationData,
) => {
    const { currentPlace, slug, placeId, isOnline } = locationData;
    const header = {
        placeName: currentPlace,
        url: `/ttd/${slug}/`,
    };
    if (isOnline || !placeId) return { header: header, tags: [] };

    const nearbyCitiesData = buckets.find((element) => {
        return element.template === NEARBY_CITIES_TEMPLATE;
    });

    const tags: { placeName: string; linkUrl: string }[] = [];
    nearbyCitiesData?.places
        ?.slice(0, 12)
        .forEach((place: ThingsToDoPlaceData) => {
            if (place.url) {
                tags.push({
                    placeName: place.name,
                    linkUrl: place.url
                        .replace('/d/', '/ttd/')
                        .replace('/events/', '/'),
                });
            }
        });

    return { header, tags };
};

export const transformBuckets = ({
    buckets,
    expanded,
}: {
    buckets: Bucket[];
    expanded: boolean;
}) => {
    //snake to camel each key
    //if events format the events
    //put events/collections/whatever under "content"
    return buckets.map(({ type, ...bucket }) => {
        let content = [];
        const contentKey = ENTITY_TYPE_MAP[type];
        const shallowCamelBucket = mapKeys(bucket, (_val, key) =>
            camelCase(key),
        );

        if (contentKey) {
            content = CARD_INFO_LOOKUP_MAP[type](
                shallowCamelBucket[contentKey],
            );
        }

        return {
            ...shallowCamelBucket,
            content,
            type,
            isContentExpanded: expanded,
        };
    });
};
